import React, { useState, useEffect, useRef } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useTranslation } from 'react-i18next';
import { NavigationBar } from '@oneaudi/unified-web-components';
import { NavConfigsOutpout } from '../../constants/navConfigs';
import useCurrentSection from '../../hooks/useCurrentSection';
import useDetectScrolledToBottom from '../../hooks/useDetectScrolledToBottom';
import FiltersIcon from '../../assets/icons/FiltersIcon';
import { device } from '../../styles/devices';
import {
  Container,
  CopyOne,
  DesktopWrapper,
  FiltersCTA,
  FilterWrapper,
  MobileWrapper,
  NavigationWrapper,
  SkipToWrapper,
  StyledNavigationBar,
  StyledSelect,
} from './SkipTo.styles';
import useScrollTracker from '../../hooks/useScrollTracker';

type StickyNavProps = {
  notScrollFlag?: boolean;
  executeScroll: (goTo: string) => void;
  navConfigs: NavConfigsOutpout;
  handleScrollFlag: (flag: boolean) => void;
  handleOpenFilters: () => void;
  isFiltersOpen: boolean;
  selectedFiltersCount: number;
  // displayFilterCTA: boolean;
  // onFiltersOpen: () => void;
};

const SkipTo: React.FC<StickyNavProps> = ({
  executeScroll,
  selectedFiltersCount,
  // displayFilterCTA,
  // onFiltersOpen,
  notScrollFlag,
  handleScrollFlag,
  navConfigs,
  handleOpenFilters,
  isFiltersOpen,
}) => {
  const { t } = useTranslation();
  const isLaptop = useMediaQuery({ query: device.laptop });
  const isTablet = useMediaQuery({ query: device.tablet });
  const isMobile = useMediaQuery({ query: '(max-width: 480px)' });

  const [currentNav, setCurrentNav] = useState('nav-default');
  const currentElementId = useCurrentSection(navConfigs, isLaptop);
  const isBottom = useDetectScrolledToBottom();
  const stickyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (notScrollFlag) return; // we shouldn't consider the return value of useCurrentSection hook when "notScroll" is TRUE
    const navId = navConfigs.find((item) => item.elementId === currentElementId)?.navId;
    const isDisabled = navConfigs.find((item) => item.elementId === currentElementId)?.disabled;
    // Find the last item with disabled = false
    const lastEnabledSection = navConfigs
      .slice()
      .reverse()
      .find((item) => !item.disabled);

    if (isBottom) {
      // when scroll reached to the end of the page we set last item of navConfigs as the selected Nav
      if (lastEnabledSection) {
        setCurrentNav(lastEnabledSection.navId);
      }
      return;
    }
    if (navId && navId.length > 0 && !isDisabled) {
      setCurrentNav(navId);
    } else {
      setCurrentNav('');
    }
  }, [currentElementId, isBottom, navConfigs, notScrollFlag]);

  const handleChangeNav = (id: string) => {
    // when user changes a nav by selecting a nav from nav bar or select component
    // it means we should move to that section regardless of scroll position
    // so we set "setNotScroll" to "True"
    handleScrollFlag(true);
    const goTo = navConfigs.find((item) => item.navId === id)?.elementId || '';
    executeScroll(goTo);

    const scrollHandler = (e: Event) => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        // When scroll ends we "SetNotScroll" false again which means we can again track scroll position
        const isDisabled = navConfigs.find((item) => item.elementId === id)?.disabled;
        if (!isDisabled) {
          setCurrentNav(id);
        }
        handleScrollFlag(false);

        window.removeEventListener('scroll', scrollHandler);
      }, 200);
    };

    let scrollTimeout: ReturnType<typeof setTimeout>;

    window.addEventListener('scroll', scrollHandler);
  };

  const mobileScrolled = useScrollTracker(340);

  const isSticky = mobileScrolled && isMobile;

  return (
    <SkipToWrapper>
      <Container ref={stickyRef} isStuck={isSticky} isFiltersOpen={isFiltersOpen}>
        {!isLaptop && (
          <FilterWrapper>
            <FiltersCTA data-testid="filters-cta" onClick={handleOpenFilters}>
              <FiltersIcon />
              {isTablet ? t('promo.filters.openFilterCta') : t('promo.filters.openFilterCtaMobile')}
              {!!selectedFiltersCount && ` (${selectedFiltersCount})`}
            </FiltersCTA>
          </FilterWrapper>
        )}

        {!isSticky && (
          <>
            <DesktopWrapper>
              <CopyOne>{t('promo.stickyNav.labelDesktop')}:</CopyOne>
              <NavigationWrapper>
                <NavigationBar
                  id="nav-bar-skip-to"
                  size="md"
                  scrollButtons={{
                    left: {
                      ariaLabel: 'Scroll Back',
                    },
                    right: {
                      ariaLabel: 'Scroll Forward',
                    },
                  }}
                  items={navConfigs.map((config) => {
                    return {
                      label: config.label,
                      disabled: config.disabled,
                      active: currentNav === config.navId,
                      onClick: () => {
                        handleChangeNav(config.navId);
                      },
                    };
                  })}
                />
              </NavigationWrapper>
            </DesktopWrapper>
            <MobileWrapper>
              <StyledSelect
                // inputId="mobile-select"
                id="mobile-select"
                label={
                  isTablet ? t('promo.stickyNav.labelTablet') : t('promo.stickyNav.labelMobile')
                }
                value={currentNav !== 'nav-default' ? currentNav : ''}
                // value={currentNav}
                onChange={(e) => {
                  handleChangeNav(e.target.value);
                }}
                className={currentNav.length > 0 && currentNav !== 'nav-default' ? 'hasValue' : ''}
                // hideLabelOptional
              >
                {navConfigs.map((item) => {
                  if (item.navId === 'nav-default') return null;
                  return (
                    <option
                      disabled={item.disabled}
                      value={item.navId}
                      key={`select-${item.navId}`}
                      id={`mobile-${item.navId}`}
                    >
                      {item.label}
                    </option>
                  );
                })}
              </StyledSelect>
            </MobileWrapper>
          </>
        )}
      </Container>
    </SkipToWrapper>
  );
};

export default SkipTo;
